import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { HttpClient } from '@angular/common/http';
import { Utils } from '../../../../../utils/utils';
import { HistoryColumnConfig, HistoryDialogConfig } from 'src/app/interfaces/branchexcellence/historyDialog';

@Component({
    selector: 'focusplan-history-dialog',
    templateUrl: './focusplan-history-dialog.component.html',
    styleUrls: ['./focusplan-history-dialog.component.less'],
    standalone: false
})
export class FocusPlanHistoryDialogComponent implements OnInit {

    dataSource = new MatTableDataSource<any>();
    displayedColumns: string[] = [];
    isCallSucceed: number = 0;
    config: HistoryDialogConfig;
    
    selectedYear: string = '';
    selectedPeriod: string = '';
    availableYears: number[] = [];
    availablePeriods: number[] = [];
    originalData: any[] = [];

    constructor(
        public dialogRef: MatDialogRef<FocusPlanHistoryDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: HistoryDialogConfig,
        private http: HttpClient,
        @Inject('BASE_URL') private baseUrl: string,
    ) {
        this.config = data;
    }

    ngOnInit(): void {
        this.setupColumns();
        this.loadHistory();
    }

    setupColumns(): void {
        this.displayedColumns = this.config.columns.map(col => col.key);
    }

    getColumnConfig(key: string): HistoryColumnConfig | undefined {
        return this.config.columns.find(col => col.key === key);
    }

    shouldShowColumn(columnConfig: HistoryColumnConfig): boolean {
        if (!columnConfig.condition) return true;
        
        if (columnConfig.condition.includes('measure')) {
            return this.config.entityType === 'measure';
        }
        if (columnConfig.condition.includes('actionitem')) {
            return this.config.entityType === 'actionitem';
        }
        if (columnConfig.condition.includes('impact')) {
            return this.config.entityType === 'impact';
        }
        if (columnConfig.condition.includes('status')) {
            return this.config.entityType === 'status';
        }
        
        return true;
    }

    getOperationClass(operation: string): string {
        return 'operation-badge';
    }

    formatCellValue(element: any, columnConfig: HistoryColumnConfig): string {
        const value = element[columnConfig.key];
        
        switch (columnConfig.type) {
            case 'date':
                return value ? new Date(value).toLocaleDateString('en-GB') + ' ' + 
                              new Date(value).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '-';
            case 'operation':
                return value === 0 ? 'NEW' : 'SAVED';
            case 'text':
                if (columnConfig.key === 'isHardcoded') {
                    return value === true ? 'Yes' : 'No';
                }
                 if (columnConfig.key === 'status') {
                    return value === 0 ? 'NEW' : 'SAVED';
                }
                if (columnConfig.key === 'value' && typeof value === 'number') {
                    return value.toFixed(2);
                }
                if (columnConfig.key === 'period' && typeof value === 'number') {
                    return this.formatPeriod(value);
                }
                if (columnConfig.key === 'fiscalYear' && typeof value === 'number') {
                    return this.formatFiscalYear(value);
                }
                return value || '-';
            default:
                return value || '-';
        }
    }

    formatPeriod(period: number): string {
        const periodMap: { [key: number]: string } = {
            1: 'OCT',
            2: 'NOV', 
            3: 'DEC',
            4: 'JAN',
            5: 'FEB',
            6: 'MAR',
            7: 'APR',
            8: 'MAY',
            9: 'JUN',
            10: 'JUL',
            11: 'AUG',
            12: 'SEP'
        };
        return periodMap[period] || period.toString();
    }

    formatFiscalYear(fiscalYear: number): string {
        return `${fiscalYear - 1}/${fiscalYear}`;
    }

    loadHistory(): void {
        
        const utilsFunction = Utils[this.config.loadHistoryFunction];
        if (utilsFunction && typeof utilsFunction === 'function') {
            utilsFunction.call(Utils,
                this.http,
                this.baseUrl,
                this.config.id,
                this,
                this.onHistoryLoaded
            );
        } else {
            console.error(`Function ${this.config.loadHistoryFunction} not found in Utils`);
        }
    }

    onHistoryLoaded(that: FocusPlanHistoryDialogComponent, result: any[]): void {
        that.originalData = result;
        that.dataSource.data = result;
        
        
        if (that.config.entityType === 'impact') {
            that.setupFilters();
        }
    }
    
    setupFilters(): void {
       
        const years = [...new Set(this.originalData.map(item => item.fiscalYear))].filter(year => year != null).sort((a, b) => b - a);
        const periods = [...new Set(this.originalData.map(item => item.period))].filter(period => period != null).sort((a, b) => a - b);
        
        this.availableYears = years;
        this.availablePeriods = periods;
    }
    
    applyFilters(): void {
        let filteredData = [...this.originalData];
        
        if (this.selectedYear) {
            filteredData = filteredData.filter(item => item.fiscalYear == this.selectedYear);
        }
        
        if (this.selectedPeriod) {
            filteredData = filteredData.filter(item => item.period == this.selectedPeriod);
        }
        
        this.dataSource.data = filteredData;
    }
    
    clearFilters(): void {
        this.selectedYear = '';
        this.selectedPeriod = '';
        this.dataSource.data = this.originalData;
    }

    onClose(): void {
        this.dialogRef.close();
    }

    static createMeasureConfig(element: any): HistoryDialogConfig {
        return {
            element: element,
            id: element.MeasureId,
            type: 'Measure',
            entityType: 'measure',
            loadHistoryFunction: 'httpGetMeasureHistory',
            columns: [
                { key: 'changedBy', header: 'User', type: 'text' },
                { key: 'name', header: 'Name', type: 'text' },
                { key: 'description', header: 'Description', type: 'text' },
                { key: 'owner', header: 'Owner', type: 'text' },
                { key: 'kpiName', header: 'KPI', type: 'text' },
                { key: 'changedAtUtc', header: 'Changed At (UTC)', type: 'date' },
                { key: 'operation', header: 'Status', type: 'operation' }
            ]
        };
    }

    static createActionItemConfig(element: any): HistoryDialogConfig {
        return {
            element: element,
            id: element.ActionItemId,
            type: 'Action Item',
            entityType: 'actionitem',
            loadHistoryFunction: 'httpGetActionItemHistory',
            columns: [
                { key: 'changedBy', header: 'User', type: 'text' },
                { key: 'name', header: 'Name', type: 'text' },
                { key: 'description', header: 'Description', type: 'text' },
                { key: 'owner', header: 'Owner', type: 'text' },
                { key: 'comments', header: 'Comments', type: 'text' },
                { key: 'deadline', header: 'Deadline', type: 'date' },
                { key: 'changedAtUtc', header: 'Changed At (UTC)', type: 'date' },
                { key: 'operation', header: 'Status', type: 'operation' }
            ]
        };
    }

    static createImpactConfig(element: any): HistoryDialogConfig {
        return {
            element: element,
            id: element.MeasureId,
            type: 'Financial Impact',
            entityType: 'impact',
            loadHistoryFunction: 'httpGetMeasureTargetHistory',
            columns: [
                { key: 'user', header: 'User', type: 'text' },
                { key: 'date', header: 'Date', type: 'date' },
                { key: 'fiscalYear', header: 'Year', type: 'text' },
                { key: 'period', header: 'Period', type: 'text' },
                { key: 'isHardcoded', header: 'Is Hardcoded', type: 'text' },
                { key: 'value', header: 'Value', type: 'text' },
                { key: 'status', header: 'Status', type: 'text' }
            ]
        };
    }

    static createMeasureStatusConfig(element: any): HistoryDialogConfig {
        return {
            element: element,
            id: element.MeasureId,
            type: 'Measure Status',
            entityType: 'measure',
            loadHistoryFunction: 'httpGetMeasureStatusHistory', 
            columns: [
                { key: 'changedBy', header: 'User', type: 'text' },
                { key: 'statusQXDesc', header: 'Measure Status', type: 'text' },
                { key: 'changedAtUtc', header: 'Changed At (UTC)', type: 'date' },
                { key: 'operation', header: 'Status', type: 'operation' }
            ]
        };
    }

    static createActionStatusConfig(element: any): HistoryDialogConfig {
        return {
            element: element,
            id: element.ActionItemId,
            type: 'Action Item Status',
            entityType: 'actionitem',
            loadHistoryFunction: 'httpGetActionItemStatusHistory',
            columns: [
                { key: 'changedBy', header: 'User', type: 'text' },
                { key: 'statusQ1Desc', header: 'Check-in 1', type: 'text' },
                { key: 'statusQ2Desc', header: 'Check-in 2', type: 'text' },
                { key: 'statusQ3Desc', header: 'Check-in 3', type: 'text' },
                { key: 'statusQ4Desc', header: 'Check-in 4', type: 'text' },
                { key: 'finalStatusDesc', header: 'Final Status', type: 'text' },
                { key: 'statusComments', header: 'Status Comments', type: 'text' },
                { key: 'changedAtUtc', header: 'Changed At (UTC)', type: 'date' },
                { key: 'operation', header: 'Status', type: 'operation' }
            ]
        };
    }
} 